import AuthCli from './AuthCli'

/**
 * JavaScript version of the Cycle service API.
 */
export default class CycleCli {

  //
  // Path constants:
  //
  static get SERVICE_ROUTE() { return 'cycle' }
  static get CYCLES_PATH() { return 'cycles' }
  static get FACTORY_CYCLES_PATH() { return 'factorycycles' }
  static get FACTORY_PATH() { return 'factory' }
    
  static get PRINTING_FILE_PATH() { return 'printingfile' }
  static get FILE_CONTENT_PATH() { return 'filecontent' }
  static get SHIPMENT_PATH() { return 'shipment' }
  static get PAGE_SIZE_QUERY() { return 'pagesize' }
  static get PAGE_QUERY() { return 'page' }
  static get INCLUDE_SHIPMENT_CODES_QUERY() { return 'includeshipmentcodes' }
  static get SCHEDULE_PATH() { return "schedule" }
  static get NEXT_CLOSURE_PATH() { return "nextclosure" }
  static get FACTORY_SCHEDULE_PATH() { return "factoryschedule" }
  static get SHIPMENTS_PATH() { return "shipments" }
  static get OPEN_CYCLE_PATH() { return "opencycle" }
  static get FACTORY_OPEN_CYCLE_PATH() { return "factoryopencycle" }
  static get COUNT_PATH() { return "count" }
  static get ASSIGNED_CODES_PATH() { return "assignedcodes" }
  static get SHIPMENT_CODES_QUERY() { return "shipmentcodes" }
  static get STATS_PATH() { return "stats" }
  static get SHIPMENTS_FILTERED_PATH() { return "shipmentsfiltered" }

  // URL prefix for debugging environment vs behind reverse proxy environment.
  static get devPrefix() { return (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? 'http://localhost:6010/' : window.location.protocol + '//' + window.location.host + '/api/' }

  // TODO: other methods.

  //
  // Factory management:
  //

  /**
   * Provides the information of a factory given its internal identifier.
   * @returns {Factory}
   */
  static async GetFactoryById(factoryId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_PATH + '/' + factoryId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of all products available in the system.
   */
  static async GetAllFactories() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
  
  /**
   * Registeres a new factory into the platform or modifies its definition.
   * @param {Factory} factory Definition of the factory. If unique identifier)
   * @returns {string} Unique identifier assigned to the factory
   */
  static async StoreFactory(factory) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE
    var body = factory

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Removes an existing factory from the platform.
   * A factory cannot be removed if there are cycles closed in it.
   * @param {string} factoryId Internal unique identifier of the factory to be removed
   */
  static async DeleteFactory(factoryId) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + factoryId
    var body = null
    
    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  //
  // Cycles:
  //

  /**
   * Provides the CRON expression of the current periodic cycle closures.
   * The schedule refers to the printing factory the calling user belongs to.
   */
  static async GetSchedule() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SCHEDULE_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the CRON expression of the current periodic cycle closures.
   * The schedule refers to the specified printing factory.
   * This method is available for system administrators.
   * @param {int} factoryId Internal identifier of the factory asking for
   */
  static async GetScheduleOfFactory(factoryId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_SCHEDULE_PATH + '/' + factoryId
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the time-stamp in the future to occur the next cycle closure
   * according to current schedule.
   * The schedule refers to the printing factory the calling user belongs to.
   * @param {*} after Pass 0 to retrieve the immediately next closure. Pass 1 to retrive the closure next to the immediately next. And so on
   * @returns {DateTime} Time-stamp in the future to occur one of the next cycle closures. Null if never
   */
  static async GetNextClosure(after) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.SCHEDULE_PATH + '/' + this.NEXT_CLOSURE_PATH + '/' + after
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the time-stamp in the future to occur the next cycle closure
   * according to current schedule.
   * The schedule refers to the printing factory specified.
   * This method is available for system administrators.
   * @param {*} after Pass 0 to retrieve the immediately next closure. Pass 1 to retrive the closure next to the immediately next. And so on
   * @param {int} factoryId Internal identifier of the factory asking for
   * @returns {DateTime} Time-stamp in the future to occur one of the next cycle closures. Null if never
   */
  static async GetNextClosureOfFactory(after, factoryId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_SCHEDULE_PATH + '/' + factoryId + '/' + this.NEXT_CLOSURE_PATH + '/' + after
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
    
  /**
   * Establishes the periodic cycle closures to be applicable from now on.
   * The schedule will be established for the printing factory of the calling user.
   * This method is available for factory administrators.
   * @param {string} cronExpression A CRON expression. Pass empty expression to disable cycle closures
   */
  static async SetSchedule(cronExpression) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.SCHEDULE_PATH
    var body = cronExpression

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Establishes the periodic cycle closures to be applicable from now on.
   * The schedule will be established for the specified printing factory.
   * This method is available for system administrators.
   * @param {string} cronExpression A CRON expression. Pass empty expression to disable cycle closures
   * @param {int} factoryId Internal identifier of the factory asking for
   */
  static async SetScheduleOfFactory(cronExpression, factoryId) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_SCHEDULE_PATH + '/' + factoryId
    var body = cronExpression

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information of some closed cycles.
   * The information is about cycles of the printing factory the calling user belongs to.
   * This is a paging request, the response may not contain all available cycles.
   * For paging considerations, the collection of cycles is provided sorted by closing time descending.
   * @param {int} pageSize Paging argument: maximum amount of cycles to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {bool} includeShipmentCodes Indicates that the returned information will fill in the array of shipment codes in the cycles, otherwise the array will be null
   * @returns {[CycleInfo]} 
   */
  static async GetCycles(pageSize, page, includeShipmentCodes) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH +
      '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
      '&' + this.PAGE_QUERY + '=' + page +
      '&' + this.INCLUDE_SHIPMENT_CODES_QUERY + '=' + includeShipmentCodes

    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information of some closed cycles.
   * The information is about cycles of the specified printing factory.
   * This method is available for system administrators.
   * This is a paging request, the response may not contain all available cycles.
   * For paging considerations, the collection of cycles is provided sorted by closing time descending.
   * @param {int} pageSize Paging argument: maximum amount of cycles to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {String} factoryId Internal identifier of the factory asking for
   * @param {bool} includeShipmentCodes Indicates that the returned information will fill in the array of shipment codes in the cycles, otherwise the array will be null
   * @returns {[CycleInfo]} 
   */
  static async GetCyclesOfFactory(pageSize, page, factoryId, includeShipmentCodes) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_CYCLES_PATH + '/' + factoryId +
      '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
      '&' + this.PAGE_QUERY + '=' + page +
      '&' + this.INCLUDE_SHIPMENT_CODES_QUERY + '=' + includeShipmentCodes

    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about one consolidation cycle of the
   * factory the calling user is working in.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {bool} includeShipmentCodes Indicates that the returned information will fill in the array of shipment codes in the cycles, otherwise the array will be null 
   * @returns {CycleInfo}
   */
  static async GetCycle(cycleIdOrCode, includeShipmentCodes) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH + '/' + cycleIdOrCode + '?' + this.INCLUDE_SHIPMENT_CODES_QUERY + '=' + includeShipmentCodes
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the information about one consolidation cycle of a given factory.
   * The calling user must be system administrator.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {String} factoryId Internal identifier of the factory the cycle must belong to
   * @param {bool} includeShipmentCodes Indicates that the returned information will fill in the array of shipment codes in the cycles, otherwise the array will be null 
   * @returns {CycleInfo}
   */
  static async GetCycleOfFactory(cycleIdOrCode, factoryId, includeShipmentCodes) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_CYCLES_PATH + '/' + factoryId + '/' + cycleIdOrCode + '?' + this.INCLUDE_SHIPMENT_CODES_QUERY + '=' + includeShipmentCodes
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the shipments that where included for consolidation in a cycle.
   * The cycle belongs to the factory the calling user is working on.
   * This is a paging request, the response may not contain all available shipments.
   * For paging considerations, the collection of shipments is provided sorted by shipment code alphabetically.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @returns {String[]} Shipment codes (external identifier)
   */
  static async GetShipmentsInCycle(cycleIdOrCode, pageSize, page) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH + '/' + cycleIdOrCode + '/' + this.SHIPMENTS_PATH +
      '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
      '&' + this.PAGE_QUERY + '=' + page
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
  *Provides some of the shipments that where included for consolidationi in a cycle, filtered by some conditions.
  * The cycle belongs to the factory the calling user is working on.
  * This is a paging request, the response may not contain all available shipments.
  * For paging considerations, the collection of shipments is provided sorted by shipment code alphabetically.
  * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
  * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
  * @param {int} page Paging argument: the page to provide, 0-based
  * @param {int} shipmentFilter Conditions for the provided shipments, based on shipment features and meta-data
  * @param {int} processingFilter Conditions for the provided shipments, based on processing stages
  * @param {int} order How provided shipments will be sorted
  * @returns {String[]} Shipment codes (external identifier)
  */
  static async GetShipmentsInCycleFiltered(cycleIdOrCode, pageSize, page, shipmentFilter, processingFilterIn, order) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH + '/' + cycleIdOrCode + '/' + this.SHIPMENTS_PATH +
      '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
      '&' + this.PAGE_QUERY + '=' + page
    //TODO Path constants
    /* '&' + this.PAGE_QUERY + '=' + shipmentFilter
    '&' + this.PAGE_QUERY + '=' + processingFilterIn
    '&' + this.PAGE_QUERY + '=' + order */
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }


  /**
   * Provides the shipments that where included for consolidation in a cycle.
   * The cycle belongs to the factory specified.
   * This method is for system administrators.
   * This is a paging request, the response may not contain all available shipments.
   * For paging considerations, the collection of shipments is provided sorted by shipment code alphabetically.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {String} factoryId Internal identifier of the factory the cycle must belong to
   * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @returns {String[]} Shipment codes (external identifier)
   */
  static async GetShipmentsInCycleOfFactory(cycleIdOrCode, factoryId, pageSize, page) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_CYCLES_PATH + '/' + factoryId + '/' + cycleIdOrCode + '/' + this.SHIPMENTS_PATH +
      '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
      '&' + this.PAGE_QUERY + '=' + page
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the shipments that where included for consolidation in a cycle.
   * The cycle belongs to the factory specified.
   * This method is for system administrators.
   * This is a paging request, the response may not contain all available shipments.
   * For paging considerations, the collection of shipments is provided sorted by shipment code alphabetically.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {String} factoryId Internal identifier of the factory the cycle must belong to
   * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
   * @param {int} page Paging argument: the page to provide, 0-based
   * @param {int} shipmentFilter Conditions for the provided shipments, based on shipment features and meta-data
   * @param {int} processingFilter Conditions for the provided shipments, based on processing stages
   * @param {int} order How provided shipments will be sorted
   * @returns {String[]} Shipment codes (external identifier)
   */
  static async GetShipmentsInCycleOfFactoryFiltered(cycleIdOrCode, factoryId, pageSize, page) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_CYCLES_PATH + '/' + factoryId + '/' + cycleIdOrCode + '/' + this.SHIPMENTS_PATH +
      '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
      '&' + this.PAGE_QUERY + '=' + page
    //TODO Path constants
    /* '&' + this.PAGE_QUERY + '=' + shipmentFilter
    '&' + this.PAGE_QUERY + '=' + processingFilterIn
    '&' + this.PAGE_QUERY + '=' + order */
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Calculates counts and statistics about a consolidation cycle for all or some of the shipments included.
   * The cycle belongs to the factory the calling user is working on.
   * @param {string} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {ShipmentFilter} shipmentFilter Conditions for the shipments to be included in the calculations, based on shipment features and meta-data
   * @param {ProcessingFilter} processingFilterIn Conditions for the shipments to be included in the calculations, based on processing stages
   * @param {ProcessingFilter} processingFilterOut Conditions for the shipments to be excluded from the calculations, based on processing stages
   * @returns {CycleStats} Counts and statistics calculated
   */
  static async GetCycleStats(cycleIdOrCode, filters) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH + '/' + cycleIdOrCode + '/' + this.STATS_PATH
    var body = filters

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**Calculates counts and statistics about a consolidation cycle for all or some of the shipments included.
   * The cycle belongs to the factory specified.
   * This method is for system administrators.
        /// </summary>
   * @param {string} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @param {string} factoryId 
   * @param {ShipmentFilter} shipmentFilter Conditions for the shipments to be included in the calculations, based on shipment features and meta-data
   * @param {ProcessingFilter} processingFilter Conditions for the shipments to be included in the calculations, based on processing stages
   * @returns {CycleStats} Counts and statistics calculated
   */
  static async GetCycleStatsOfFactory(cycleIdOrCode, factoryId, filters) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_CYCLES_PATH + '/' + factoryId + '/' + cycleIdOrCode + '/' + this.STATS_PATH
    var body = filters

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  

  /**
     * Provides the shipments that are enqueued for a following consolidation
     * in a cycle to belong to the factory the calling user is working on.
     * This is a paging request, the response may not contain all available shipments.
     * For paging considerations, the collection of shipments is provided sorted by shipment code alphabetically.
     * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
     * @param {int} page Paging argument: the page to provide, 0-based
     * @param {ShipmentFilter} filter Conditions for those shipments to be included. Pass null for no filter
     * @returns {String[]} Shipment codes (external identifier)
     */
    static async GetShipmentsInOpenCycle(pageSize, page, filter) {
      var verb = 'PUT';
      var path = this.SERVICE_ROUTE + '/' + this.OPEN_CYCLE_PATH +
        '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
        '&' + this.PAGE_QUERY + '=' + page;
      var body = filter;
  
      return AuthCli.Fetch(this.devPrefix + path, verb, body);
    }
  
    /**
     * Provides the shipments that are enqueued for a following consolidation
     * in a cycle to belong to the factory specified.
     * This method is for system administrators.
     * This is a paging request, the response may not contain all available shipments.
     * For paging considerations, the collection of shipments is provided sorted by shipment code alphabetically.
     * @param {String} factoryId Internal identifier of the factory the cycle must belong to
     * @param {int} pageSize Paging argument: maximum amount of shipments to be provided in this call
     * @param {int} page Paging argument: the page to provide, 0-based
     * @param {ShipmentFilter} filter Conditions for those shipments to be included. Pass null for no filter
     * @returns {String[]} Shipment codes (external identifier)
     */
    static async GetShipmentsInOpenCycleOfFactory(factoryId, pageSize, page, filter) {
      var verb = 'PUT';
      var path = this.SERVICE_ROUTE + '/' + this.FACTORY_OPEN_CYCLE_PATH + '/' + factoryId +
        '?' + this.PAGE_SIZE_QUERY + '=' + pageSize +
        '&' + this.PAGE_QUERY + '=' + page;
      var body = filter;
  
      return AuthCli.Fetch(this.devPrefix + path, verb, body);
    }
  

  /**
   * Provides all codes generated and assigned during the processing of each of the specified shipments.
   * Example of these codes are: tracking code, expedition code... the codes included depend on the kind of consolidation applied..
    @param {string[]} shipmentCodes External identifiers of shipments asking for their burofaxes. Comma-separated list in REST request, like "PXXDMYXXM"
  */
  static async GetAssignedCodes(shipmentCodes) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.ASSIGNED_CODES_PATH
    var body = shipmentCodes

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Calculates the number of shipments that are enqueued for a following consolidation
   * in a cycle to belong to the factory the calling user is working on.
   */
  static async CountShipmentsInOpenCycle() {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.OPEN_CYCLE_PATH + '/' + this.COUNT_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Calculates the number of shipments that are enqueued for a following consolidation
   * in a cycle to belong to the factory the calling user is working on,
   * considering only those matching the provided filter.
   * @param {ShipmentFilter} filter Conditions for those shipments to be counted. Pass null for no filter
   */
  static async CountShipmentsInOpenCycleFiltered(filter) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.OPEN_CYCLE_PATH + '/' + this.COUNT_PATH
    var body = filter

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }



  static async CountShipmentsInOpenCycleOfFactory(factoryId) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_OPEN_CYCLE_PATH + '/' + factoryId + '/' + this.COUNT_PATH
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Calculates the number of shipments that are enqueued for a following consolidation
   * in a cycle to belong to the specified factory,
   * considering only those matching the provided filter.
   * This method is for system administrators.
   * @param {String} factoryId Internal identifier of the factory the cycle must belong to
   * @param {ShipmentFilter} filter Conditions for those shipments to be counted. Pass null for no filter
   */
  static async CountShipmentsInOpenCycleOfFactoryFiltered(factoryId, filter) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.FACTORY_OPEN_CYCLE_PATH + '/' + factoryId + '/' + this.COUNT_PATH
    var body = filter

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Provides the list of all printing files generated in a consolidation cycle.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
   * @returns {PrintingFile} Information about printing files generated for the cycle
   */
  static async GetPrintingFiles(cycleIdOrCode) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.PRINTING_FILE_PATH + '/' + cycleIdOrCode
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Downloads the content of a printing file generated in a consolidation cycle.
   * The file content is retrieved from the service and forced to appear in the browser
   * as a downloaded file.
   * @param {String} printingFileId Internal identifier of the printing file
   * @param {String} fileName File name with extension as expected to be stored in local file system.
   */
  static async DownloadPrintingFile(printingFileId, fileName) {
    var verb = 'GET'
    var path = this.SERVICE_ROUTE + '/' + this.PRINTING_FILE_PATH + '/' + printingFileId + '/' + this.FILE_CONTENT_PATH
    var body = null

    return fetch(this.devPrefix + path, AuthCli.GetRequestOptions(verb, body, true))
      .then(response => {
        if (response.status === 200) return response.blob()
        else if (response.status === 500) return response.text()
      })
      .then(blobby => {
        if (blobby == null) return
        if (blobby.toString().startsWith('[ERR')) return blobby.toString()

        // Force the received content to appear as a downloaded file.
        let anchor = document.createElement("a");
        document.body.appendChild(anchor);
        let objectUrl = window.URL.createObjectURL(blobby);
        anchor.href = objectUrl;
        anchor.download = fileName;
        anchor.click();
        window.URL.revokeObjectURL(objectUrl);
      });
  }

  /**
   * Initiates a consolidation cycle considering all shipments pending to be consolidated
   * for a printing factory.
   * This call returns once the initial stage of a consolidation is complete but pending
   * to run the time-consuming stage of generating printing files.
   * The header of this call will include the validation token as "Authorization: Bearer validationToken".
   * @param {WakeupEvent} eventInfo Callback information from the sched service: appointmentId, payload, next
   * @returns TThe amount of shipments actually included in the consolidation cycle
   */
  static async CloseCycle(eventInfo) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.CLOSURE_PATH
    var body = eventInfo

    return this.Fetch(this.devPrefix + path, verb, body)

  }

  /**
  *Initiates a consolidation cycle considering some shipments of those pending to be consolidated
  * for the printing factory of the calling user, those fullfiling the specified filters.
  * This call returns once the initial stage of a consolidation is complete but pending
  * to run the time-consuming stage of generating printing files.
  * @param {CycleFilters} cycleFilters Conditions to apply to the shipments available and pending for consolidation
  * @returns TThe amount of shipments actually included in the consolidation cycle
  */
  static async CloseCycleFiltered(cycleFilters) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.CLOSURE_FILTERED_PATH
    var body = cycleFilters

    return this.Fetch(this.devPrefix + path, verb, body)

  }

  /**
  * Initiates a consolidation cycle considering some shipments of those pending to be consolidated
  * for a printing factory, those fullfiling the specified filters.
  * This call returns once the initial stage of a consolidation is complete but pending
  * to run the time-consuming stage of generating printing files.
  * This method is available for system administrators.
  * @param {CycleFilters} cycleFilters Conditions to apply to the shipments available and pending for consolidation
  * @param {String} factoryId Internal identifier of the factory to close cycle for
  * @returns TThe amount of shipments actually included in the consolidation cycle
  */
  static async CloseCycleFilteredFor(cycleFilters, factoryId) {
    var verb = 'POST'
    var path = this.SERVICE_ROUTE + '/' + this.CLOSURE_FILTERED_PATH + '/' + factoryId
    var body = cycleFilters

    return this.Fetch(this.devPrefix + path, verb, body)

  }

  /**
   * Attempts to stop a consolidation process in progress.
   * This applies for the consolidation in progress in the factory.
   * The aborted cycle remains as an existing closed cycle. Undo it to delete it
   * @param {String} cycleIdOrCode Internal identifier or external code of the consolidation cycle to about
   * @returns Whether the abort has been attempted, otherwise the cycle had previously finished and so not abort was possible
   */
  static async AbortClosure(cycleIdOrCode) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH
    var body = cycleIdOrCode

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
  
  /**
    * All shipments included in a given cycle will be enqueued again as if never consolidated.
    * The cycle becomes as if never closed.
    * If the cycle closure is in progress the undo will fail until completed or aborted.
    * @param {String} cycleIdOrCode Internal identifier or external code of the cycle asking for
    */
  static async UndoCycle(cycleIdOrCode) {
    var verb = 'DELETE'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH + '/' + cycleIdOrCode
    var body = null

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Attempts to stop a consolidation process in progress.
   * Indicated shipments will be extracted from the consolidation in a cycle and enqueued back
   * to be available for a later consolidation cycle.
   * @param {String} cycleIdOrCode Internal identifier or external code of the consolidation cycle to about
   * @param {String[]} shipmentCodes Codes of shipments to be extracted
   * @returns 
  */
  static async ExtractShipmentsFromCycle(cycleIdOrCode, shipmentCodes) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.CYCLES_PATH + '/' + cycleIdOrCode
    var body = shipmentCodes

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }
  
  /**
   * Sets or unsets a printing file as already printed.
   * To be called by a factory operator when a printing file has just been sent to the printer
   * and manually confirmed that the whole file has been printed in all its pages.
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle containing the printing file to be flagged
   * @param {String} printingFileId Internal identifier of the printing file to be flagged
   * @param {bool} printed Whether to be flagged as printed (true) or remove the flag (false)
   * @returns Number of shipments flagged, those included in the printing file
   */
  static async FlagAsPrinted(cycleIdOrCode, printingFileId, printed) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.PRINTING_FILE_PATH + '/' + cycleIdOrCode + '/' + printingFileId
    var body = printed

    return AuthCli.Fetch(this.devPrefix + path, verb, body)
  }

  /**
   * Sets a shipment as scanned for shipout.
   * To be called by a factory operator when scanning all printed documents (the printed bar code).
   * Note: the bar code to scan refers here to the one common to all shipments (not the carrier's code but
   * a common bar coding the shipment code PXXXXXXM).
   * @param {String} cycleIdOrCode Internal identifier or external code of the cycle containing the shipment to be flagged
   * @param {String} shipmentCode Scanned text as detected by the bar code scanner hardware
   * @returns Shipment internal identifier
   */
  static async FlagAsShippedOut(cycleIdOrCode, shipmentCode) {
    var verb = 'PUT'
    var path = this.SERVICE_ROUTE + '/' + this.SHIPMENT_PATH + '/' + cycleIdOrCode
    var body = shipmentCode

    return AuthCli.Fetch(this.devPrefix + path, verb, body)

  }
}


// WEBPACK FOOTER //
// ./src/sdk/CycleCli.js